import { useEffect, useState } from "react";
import { Platform, Pressable, StyleSheet, Text } from "react-native";

import { PUBLIC_ENV } from "@/config/public";
import { useTheme } from "@/hooks/use-theme";
import { isStayVisibleRunning, readStayVisibleDiagnostics } from "@/lib/stay-visible";
import { useStayVisibleStore } from "@/state/stay-visible-store";
import { summariseDiagnostics } from "@/lib/stay-visible-diagnostics";

/**
 * How often the OS is actually waking the stay-visible task, read off the phone.
 *
 * ## Why this exists as UI at all
 *
 * The question is iOS-only — Android already has a guaranteed one-a-minute
 * cadence — and the two ways this repository already reads numbers off a device
 * are both unavailable for it. `timing-probe.ts` writes to the console and is
 * read with `adb logcat`, which is Android; and Sentry Logs are off by policy
 * (`SENTRY_DATA_POLICY.md` §4). So the number has to be legible on the handset
 * itself, and this is the smallest surface that does that.
 *
 * ## Why it is not in production
 *
 * Gated on the environment rather than `__DEV__`, deliberately: the measurement
 * has to run on a real sideloaded build with a real background task over real
 * time, and `__DEV__` is false in exactly that build. `PUBLIC_ENV` is baked at
 * config-generation, so this cannot reach prod.
 *
 * ## Why the copy is not translated
 *
 * It is scaffolding for one question, removed once answered — the same rule
 * `timing-probe.ts` sets: "remove call sites when their question is answered"
 * rather than leaving a flag nobody flips. Seven locale entries for a string
 * with a known deletion date would be worse than an untranslated dev-only line.
 */
export function StayVisibleDiagnosticsRow() {
  const c = useTheme();
  const [line, setLine] = useState<string>("…");
  const [tick, setTick] = useState(0);

  // Read in a `.then` callback rather than by awaiting an async function that
  // then sets state — the latter is a synchronous setState inside an effect as
  // far as the cascading-render rule is concerned. `stay-visible-modal.tsx`
  // resolves the identical problem the identical way. Tapping bumps `tick`,
  // which re-runs this; nothing polls.
  useEffect(() => {
    let cancelled = false;
    void Promise.all([readStayVisibleDiagnostics(), isStayVisibleRunning()]).then(
      ([diagnostics, running]) => {
        if (cancelled) return;
        // `mode` and `running` come FIRST, and they are the two that actually
        // matter: `mode` gates every path that starts the task, so a task that
        // was never started reads identically to one the OS never woke. Wake
        // counts alone could not tell those apart, which is most of why this
        // took as long as it did to find.
        const { mode, demotedFrom, resumesUsed } = useStayVisibleStore.getState();
        setLine(
          [
            `mode=${mode}${demotedFrom ? ` (demoted from ${demotedFrom})` : ""}`,
            `task=${running ? "running" : "STOPPED"}`,
            `resumes=${resumesUsed}`,
            summariseDiagnostics(diagnostics, Date.now()),
          ].join(" · "),
        );
      },
    );
    return () => {
      cancelled = true;
    };
  }, [tick]);

  // Never in production, and never on web — there is no background task there.
  if (PUBLIC_ENV === "prod" || Platform.OS === "web") return null;

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => setTick((n) => n + 1)}
      style={[styles.row, { borderColor: c.border }]}
    >
      <Text style={[styles.title, { color: c.textSecondary }]}>
        stay visible · tap to refresh
      </Text>
      <Text style={[styles.value, { color: c.text }]}>{line}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: { paddingVertical: 12, paddingHorizontal: 4, gap: 4, borderTopWidth: 1 },
  title: { fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 },
  value: { fontSize: 13, fontVariant: ["tabular-nums"] },
});
